/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import {
  type ContextValue,
  Text as TextCore,
  type TextProps as TextCoreProps,
  useContextProps,
} from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { mergeStyles } from '../../utils/merge-styles/merge-styles.util';
import { splitByQuery } from '../../utils/split-by-query/split-by-query.util';

interface TextProps extends Omit<TextCoreProps, 'style' | 'className' | 'elementType'> {
  /** The size variant of the text. (Default: 16) */
  size?: keyof typeof sizeVariants;
  /** The color variant of the text. (Default: primary) */
  color?: keyof typeof colorVariants;
  /** The weight variant of the text. (Default: regular) */
  weight?: keyof typeof weightVariants;
  /** Highlight all occurrences of this string within children (case-insensitive). Only works when children is a plain string. */
  query?: string;
  /** Background color of the highlight mark. Matches the Message component variant. (Default: received) */
  highlightColor?: keyof typeof highlightColorVariants;
  style?: stylex.StyleXStyles;
}

const TextContext = createContext<ContextValue<TextProps, HTMLElement>>({});

const Text = forwardRef(({ style: styleProp, ...mergedProps }: TextProps, ref: ForwardedRef<HTMLElement>) => {
  [mergedProps, ref] = useContextProps(mergedProps, ref, TextContext);

  const {
    size = 16,
    color = 'primary',
    weight = 'regular',
    query,
    highlightColor = 'yellowLight',
    style: contextStyle,
    children,
    ...coreProps
  } = mergedProps as TextProps;

  const shouldHighlight = query && typeof children === 'string';
  const segments = shouldHighlight ? splitByQuery(children, query) : null;

  return (
    <TextCore
      ref={ref}
      {...stylex.props(
        styles.base,
        sizeVariants[size],
        colorVariants[color],
        weightVariants[weight],
        letterSpacingVariants[`${weight}${size.toString()}` as keyof typeof letterSpacingVariants],
        mergeStyles(contextStyle, styleProp),
        segments && styles.highlighted,
      )}
      {...coreProps}>
      {segments
        ? segments.map((segment, index) => segment.isMatch
          ? <mark key={index} {...stylex.props(markStyles.base, highlightColorVariants[highlightColor])}>{segment.text}</mark>
          : segment.text)
        : children}
    </TextCore>
  );
});

const styles = stylex.create({
  base: {
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
  },
  highlighted: {
    display: 'inline',
  },
});

const sizeVariants = stylex.create({
  20: {
    fontSize: 20,
    lineHeight: '24px',
  },
  18: {
    fontSize: 18,
    lineHeight: '22px',
  },
  16: {
    fontSize: 16,
    lineHeight: '20px',
  },
  14: {
    fontSize: 14,
    lineHeight: '18px',
  },
  13: {
    fontSize: 13,
    lineHeight: '16px',
  },
  12: {
    fontSize: 12,
    lineHeight: '16px',
  },
  11: {
    fontSize: 11,
    lineHeight: '14px',
  },
  10: {
    fontSize: 10,
    lineHeight: '12px',
  },
  8: {
    fontSize: 8,
    lineHeight: '10px',
  },
});

const colorVariants = stylex.create({
  primary: { color: $.textPrimary },
  secondary: { color: $.textSecondary },
  tertiary: { color: $.textTertiary },
  quaternary: { color: $.textQuaternary },
  placeholder: { color: $.textPlaceholder },
  disabled: { color: $.textDisabled },
  inverted: { color: $.textInverted },
  interactive: { color: $.textInteractive },
  interactiveHover: { color: $.textInteractiveHover },
  positive: { color: $.textPositive },
  positiveHover: { color: $.textPositiveHover },
  critical: { color: $.textCritical },
  criticalHover: { color: $.textCriticalHover },
  white: { color: $.textWhite },
  black: { color: $.textBlack },
  // ! The following colors have been added because the icon uses them (ask design team to standardize colors)
  red: { color: $.colorRed50 },
  yellow: { color: $.colorSun80 },
  orange: { color: $.colorPumpkin80 },
  turquoise: { color: $.colorTurquoise80 },
  blueLight: { color: $.colorOcean80 },
  blueDark: { color: $.colorSky80 },
  violet: { color: $.colorPlum60 },
  // ! The following colors have been added because the number badge uses them (ask design team to standardize colors)
  buttonContentDisabled: { color: $.buttonContentDisabled },
  // ! The following colors have been added because the button uses them (ask design team to standardize colors)
  primaryHover: { color: $.buttonContentPrimaryHover },
  tertiaryHover: { color: $.buttonContentTertiaryHover },
  // ! The following colors have been added because the snackbar uses them (ask design team to standardize colors)
  cucumber80: { color: $.accentCucumber80 },
  tertiaryDark: { color: '#C8CFFF' },
  // ! The following colors have been added because the social login button uses them (ask design team to standardize colors)
  buttonContentTertiary: { color: $.buttonContentTertiary },
});

const weightVariants = stylex.create({
  semibold: { fontWeight: 600 },
  medium: { fontWeight: 500 },
  regular: { fontWeight: 400 },
});

const letterSpacingVariants = stylex.create({
  semibold20: { letterSpacing: -0.45 },
  medium20: { letterSpacing: -0.5 },
  regular20: { letterSpacing: -0.55 },
  semibold18: { letterSpacing: -0.4 },
  medium18: { letterSpacing: -0.45 },
  regular18: { letterSpacing: -0.5 },
  semibold16: { letterSpacing: -0.4 },
  medium16: { letterSpacing: -0.45 },
  regular16: { letterSpacing: -0.5 },
  semibold14: { letterSpacing: -0.35 },
  medium14: { letterSpacing: -0.4 },
  regular14: { letterSpacing: -0.45 },
  semibold13: { letterSpacing: -0.3 },
  medium13: { letterSpacing: -0.35 },
  regular13: { letterSpacing: -0.4 },
  semibold12: { letterSpacing: -0.3 },
  medium12: { letterSpacing: -0.35 },
  regular12: { letterSpacing: -0.4 },
  semibold11: { letterSpacing: -0.2 },
  medium11: { letterSpacing: -0.25 },
  regular11: { letterSpacing: -0.35 },
  semibold10: { letterSpacing: -0.2 },
  medium10: { letterSpacing: -0.25 },
  regular10: { letterSpacing: -0.3 },
  semibold8: { letterSpacing: 0 },
  medium8: { letterSpacing: 0 },
  regular8: { letterSpacing: 0 },
});

const markStyles = stylex.create({
  base: {
    borderRadius: 4,
    paddingBlock: 0,
    paddingInline: 0,
    color: 'inherit',
    display: 'inline',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
    width: 'fit-content',
  },
});

const highlightColorVariants = stylex.create({
  yellowLight: {
    backgroundColor: $.surfaceAttentiveMedium,
  },
  yellow: {
    backgroundColor: $.surfaceAttentive,
  },
});

export { Text, TextContext, type TextProps };
